import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQuery } from '@mint-lib/query-client';
import { useService } from '@mint-lib/routing-context';
import { GoogleAnalyticsIntegrations } from '@mint-sdk/api-integrations';

import { externalNavigateTo } from '../../utils/externalNavigation.js';

export const useGetGAIntegrations = ({ enabled }: { enabled: boolean }) => {
  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });

  const { data, isLoading } = useQuery(
    {
      ...GoogleAnalyticsIntegrations.getQueryOptions({}),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled,
    },
    queryClient,
  );

  return {
    data,
    isLoading,
  };
};

export const useGA4IntegrationOauthUpdate = ({
  integrationId,
  platform,
}: {
  integrationId: string;
  platform: string;
}) => {
  const { t } = useTranslation('@myn/integrations_v2');
  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });
  const [shouldReconnectWithGoogle, setShouldReconnectWithGoogle] =
    useState(false);

  const { data, isError, error } = useQuery(
    {
      ...GoogleAnalyticsIntegrations.getOauthQueryUpdateOptions({
        urlParams: { integrationId: integrationId || '' },
      }),
      enabled: shouldReconnectWithGoogle,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
    queryClient,
  );

  useEffect(() => {
    if (shouldReconnectWithGoogle && data?.[0]?.url) {
      setShouldReconnectWithGoogle(false);
      externalNavigateTo({ href: data[0].url });
    }
  }, [shouldReconnectWithGoogle, data]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar({
        removable: true,
        variant: 'error',
        title: t('Error'),
        subTitle:
          error.message ??
          t(
            'Something went wrong during account connection. Please try again or contact the support.',
          ),
      });
    }
  }, [isError, error]);

  const reconnectWithGoogle = useCallback(() => {
    if (integrationId && platform) {
      setShouldReconnectWithGoogle(true);
    }
  }, [integrationId, platform]);

  return { reconnectWithGoogle };
};
